/**
 * Git History Module
 * Extract commit history and file changes for time-lapse visualization
 *
 * Uses Gource-style approach: fetch A/M/D changes per commit from GitHub API
 * instead of reading git tree objects (which fail in browser shallow clones)
 */

import git, { ReadCommitResult } from 'isomorphic-git';
import { getFS } from './clone';
import type { CommitInfo, FileHistory, ProgressCallback } from '../../core/types';

/**
 * Gource-style commit change entry
 */
interface CommitChange {
  sha: string;
  timestamp: number;
  author: string;
  files: Array<{
    filename: string;
    status: 'added' | 'modified' | 'removed' | 'renamed';
    previous_filename?: string;
  }>;
}

/**
 * Fetch commit file changes from GitHub API (Gource-style)
 * This is much more reliable than reading git tree objects
 */
export async function fetchCommitChangesFromGitHub(
  owner: string,
  repo: string,
  token: string,
  options: {
    maxCommits?: number;
    onProgress?: ProgressCallback;
  } = {}
): Promise<CommitChange[]> {
  const maxCommits = options.maxCommits || 1000;
  const changes: CommitChange[] = [];
  let page = 1;
  const perPage = 100;

  console.log(`[www] Fetching commit changes from GitHub API (Gource-style)...`);

  while (changes.length < maxCommits) {
    const url = `https://api.github.com/repos/${owner}/${repo}/commits?per_page=${perPage}&page=${page}`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/vnd.github+json'
      }
    });

    if (!response.ok) {
      if (response.status === 403) {
        console.warn('[www] GitHub API rate limit, stopping at', changes.length, 'commits');
        break;
      }
      throw new Error(`GitHub API error: ${response.status}`);
    }

    const commits = await response.json();
    if (commits.length === 0) break;

    // For each commit, fetch the detailed file changes
    for (const commit of commits) {
      if (changes.length >= maxCommits) break;

      if (options.onProgress) {
        options.onProgress({
          phase: 'fetching-history',
          current: changes.length,
          total: maxCommits,
          message: `Fetching commit ${changes.length + 1}/${maxCommits}`
        });
      }

      // Fetch commit details to get file changes
      const detailUrl = `https://api.github.com/repos/${owner}/${repo}/commits/${commit.sha}`;
      const detailResponse = await fetch(detailUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/vnd.github+json'
        }
      });

      if (!detailResponse.ok) {
        console.warn(`[www] Failed to fetch commit ${commit.sha}`);
        continue;
      }

      const detail = await detailResponse.json();

      changes.push({
        sha: commit.sha,
        timestamp: new Date(commit.commit.author.date).getTime() / 1000,
        author: commit.commit.author.name,
        files: (detail.files || []).map((f: any) => ({
          filename: f.filename,
          status: f.status as 'added' | 'modified' | 'removed' | 'renamed',
          previous_filename: f.previous_filename
        }))
      });
    }

    page++;

    // Small delay to avoid rate limiting
    await new Promise(r => setTimeout(r, 50));
  }

  console.log(`[www] Fetched ${changes.length} commits with file changes`);
  return changes;
}

/**
 * Build timelapse data from GitHub API commit changes (Gource-style)
 * Much more efficient and reliable than reading git trees
 */
export async function buildTimelapseFromGitHub(
  owner: string,
  repo: string,
  token: string,
  trackedFiles: Set<string>,
  options: {
    maxCommits?: number;
    onProgress?: ProgressCallback;
  } = {}
): Promise<TimelapseData> {
  // Fetch commit changes from GitHub API
  const commitChanges = await fetchCommitChangesFromGitHub(owner, repo, token, options);

  // Sort oldest first (Gource-style: build up from nothing)
  commitChanges.sort((a, b) => a.timestamp - b.timestamp);

  // Create normalized lookup for tracked files
  const normalizedTracked = new Map<string, string>();
  for (const file of trackedFiles) {
    normalizedTracked.set(normalizePath(file), file);
  }

  // Build timelapse data
  const sortedCommits: CommitInfo[] = [];
  const filesByCommit = new Map<string, Set<string>>();
  const changesByCommit = new Map<string, { added: string[]; modified: string[]; deleted: string[] }>();

  // Current file state (Gource-style: accumulate A/M/D)
  const currentFiles = new Set<string>();

  for (const commit of commitChanges) {
    // Convert to CommitInfo format
    const commitInfo: CommitInfo = {
      oid: commit.sha,
      message: '',
      author: {
        name: commit.author,
        email: '',
        timestamp: commit.timestamp
      },
      committer: {
        name: commit.author,
        email: '',
        timestamp: commit.timestamp
      },
      parent: []
    };
    sortedCommits.push(commitInfo);

    // Process file changes
    const changes = { added: [] as string[], modified: [] as string[], deleted: [] as string[] };

    for (const file of commit.files) {
      const normalized = normalizePath(file.filename);
      const graphPath = normalizedTracked.get(normalized);

      // Only track files that are in our dependency graph
      if (!graphPath) continue;

      if (file.status === 'added') {
        currentFiles.add(graphPath);
        changes.added.push(graphPath);
      } else if (file.status === 'removed') {
        currentFiles.delete(graphPath);
        changes.deleted.push(graphPath);
      } else if (file.status === 'modified') {
        changes.modified.push(graphPath);
      } else if (file.status === 'renamed') {
        // Handle rename: remove old, add new
        if (file.previous_filename) {
          const oldNormalized = normalizePath(file.previous_filename);
          const oldPath = normalizedTracked.get(oldNormalized);
          if (oldPath) {
            currentFiles.delete(oldPath);
            changes.deleted.push(oldPath);
          }
        }
        currentFiles.add(graphPath);
        changes.added.push(graphPath);
      }
    }

    filesByCommit.set(commit.sha, new Set(currentFiles));
    changesByCommit.set(commit.sha, changes);
  }

  console.log(`[www] Gource-style timelapse: ${sortedCommits.length} commits, ${currentFiles.size} final files`);

  return { sortedCommits, filesByCommit, changesByCommit };
}

/**
 * Get all commits in the repository
 */
export async function getCommitHistory(
  dir: string,
  options: {
    depth?: number;
    since?: Date;
    ref?: string;
  } = {}
): Promise<CommitInfo[]> {
  const fs = getFS();

  const commits = await git.log({
    fs,
    dir,
    ref: options.ref || 'HEAD',
    depth: options.depth,
    since: options.since
  });

  return commits.map((c: ReadCommitResult) => ({
    oid: c.oid,
    message: c.commit.message,
    author: {
      name: c.commit.author.name,
      email: c.commit.author.email,
      timestamp: c.commit.author.timestamp
    },
    committer: {
      name: c.commit.committer.name,
      email: c.commit.committer.email,
      timestamp: c.commit.committer.timestamp
    },
    parent: c.commit.parent
  }));
}

/**
 * Get the file tree at a specific commit
 * Tries multiple methods: listFiles, then walk, then readTree
 */
export async function getFilesAtCommit(
  dir: string,
  commitOid: string
): Promise<string[]> {
  const fs = getFS();

  // Method 1: Try git.listFiles (simplest, often works best)
  try {
    const files = await git.listFiles({
      fs,
      dir,
      ref: commitOid
    });
    if (files.length > 0) {
      console.log(`[www] listFiles returned ${files.length} files for ${commitOid.slice(0, 7)}`);
      return files;
    }
  } catch (err) {
    console.log(`[www] listFiles failed for ${commitOid.slice(0, 7)}:`, err);
  }

  // Method 2: Try walking the tree (may work for some repos)
  try {
    const files: string[] = [];
    await git.walk({
      fs,
      dir,
      trees: [git.TREE({ ref: commitOid })],
      map: async (filepath, [entry]) => {
        if (entry && (await entry.type()) === 'blob') {
          files.push(filepath);
        }
        return null;
      }
    });
    if (files.length > 0) {
      console.log(`[www] walk returned ${files.length} files for ${commitOid.slice(0, 7)}`);
      return files;
    }
  } catch (err) {
    console.log(`[www] walk failed for ${commitOid.slice(0, 7)}:`, err);
  }

  // Method 3: Try reading commit and tree objects directly
  try {
    const { commit } = await git.readCommit({ fs, dir, oid: commitOid });
    const treeOid = commit.tree;
    console.log(`[www] Commit ${commitOid.slice(0, 7)} has tree ${treeOid.slice(0, 7)}`);

    const files = await walkTreeRecursive(fs, dir, treeOid, '');
    if (files.length > 0) {
      console.log(`[www] readTree returned ${files.length} files for ${commitOid.slice(0, 7)}`);
      return files;
    }
  } catch (err) {
    console.log(`[www] readTree failed for ${commitOid.slice(0, 7)}:`, err);
  }

  return [];
}

/**
 * Recursively walk a tree object to list files
 */
async function walkTreeRecursive(
  fs: any,
  dir: string,
  treeOid: string,
  prefix: string
): Promise<string[]> {
  const files: string[] = [];

  try {
    const { tree } = await git.readTree({ fs, dir, oid: treeOid });

    for (const entry of tree) {
      const path = prefix ? `${prefix}/${entry.path}` : entry.path;

      if (entry.type === 'blob') {
        files.push(path);
      } else if (entry.type === 'tree') {
        const subFiles = await walkTreeRecursive(fs, dir, entry.oid, path);
        files.push(...subFiles);
      }
    }
  } catch (err) {
    // Tree object not available
  }

  return files;
}

/**
 * Get file content at a specific commit
 */
export async function getFileContentAtCommit(
  dir: string,
  commitOid: string,
  filepath: string
): Promise<string | null> {
  const fs = getFS();

  try {
    const { blob } = await git.readBlob({
      fs,
      dir,
      oid: commitOid,
      filepath
    });

    return new TextDecoder().decode(blob);
  } catch {
    return null;
  }
}

/**
 * Get all files with their content at a specific commit
 */
export async function getFileMapAtCommit(
  dir: string,
  commitOid: string,
  filter?: (path: string) => boolean,
  onProgress?: ProgressCallback
): Promise<Map<string, string>> {
  const files = await getFilesAtCommit(dir, commitOid);
  const filteredFiles = filter ? files.filter(filter) : files;
  const fileMap = new Map<string, string>();

  let current = 0;
  const total = filteredFiles.length;

  for (const filepath of filteredFiles) {
    current++;
    if (onProgress) {
      onProgress({
        phase: 'reading-commit',
        current,
        total,
        message: `Reading ${filepath} at ${commitOid.slice(0, 7)}`
      });
    }

    const content = await getFileContentAtCommit(dir, commitOid, filepath);
    if (content !== null) {
      fileMap.set(filepath, content);
    }
  }

  return fileMap;
}

/**
 * Compare two commits and get the changes
 */
export async function getCommitDiff(
  dir: string,
  oldCommit: string,
  newCommit: string
): Promise<{
  added: string[];
  modified: string[];
  deleted: string[];
}> {
  const fs = getFS();

  const changes = {
    added: [] as string[],
    modified: [] as string[],
    deleted: [] as string[]
  };

  // Try to get file lists for both commits
  const oldFiles = await getFilesAtCommit(dir, oldCommit);
  const newFiles = await getFilesAtCommit(dir, newCommit);

  // If we got file lists, compare them
  if (oldFiles.length > 0 || newFiles.length > 0) {
    const oldSet = new Set(oldFiles);
    const newSet = new Set(newFiles);

    // For now, we can detect added/deleted but not modified without blob comparison
    for (const file of newFiles) {
      if (!oldSet.has(file)) {
        changes.added.push(file);
      }
    }
    for (const file of oldFiles) {
      if (!newSet.has(file)) {
        changes.deleted.push(file);
      }
    }

    // For files in both, try to detect modifications via blob OID comparison
    const common = newFiles.filter(f => oldSet.has(f));
    for (const filepath of common) {
      try {
        const oldBlob = await git.readBlob({ fs, dir, oid: oldCommit, filepath });
        const newBlob = await git.readBlob({ fs, dir, oid: newCommit, filepath });
        // Compare by size first (fast), then by content if sizes match
        if (oldBlob.blob.length !== newBlob.blob.length) {
          changes.modified.push(filepath);
        } else {
          // Compare first few bytes
          let different = false;
          for (let i = 0; i < Math.min(1000, oldBlob.blob.length); i++) {
            if (oldBlob.blob[i] !== newBlob.blob[i]) {
              different = true;
              break;
            }
          }
          if (different) {
            changes.modified.push(filepath);
          }
        }
      } catch {
        // Can't compare blobs, skip modification detection for this file
      }
    }

    return changes;
  }

  // Fallback: Try git.walk with TREE refs
  try {
    await git.walk({
      fs,
      dir,
      trees: [
        git.TREE({ ref: oldCommit }),
        git.TREE({ ref: newCommit })
      ],
      map: async (filepath, [oldEntry, newEntry]) => {
        const oldType = oldEntry ? await oldEntry.type() : null;
        const newType = newEntry ? await newEntry.type() : null;

        // Only track blobs (files), not trees (directories)
        if (oldType !== 'blob' && newType !== 'blob') return null;

        const oldOid = oldEntry ? await oldEntry.oid() : null;
        const newOid = newEntry ? await newEntry.oid() : null;

        if (!oldOid && newOid) {
          changes.added.push(filepath);
        } else if (oldOid && !newOid) {
          changes.deleted.push(filepath);
        } else if (oldOid !== newOid) {
          changes.modified.push(filepath);
        }

        return null;
      }
    });
  } catch (err) {
    console.log(`[www] git.walk diff failed:`, err);
  }

  return changes;
}

/**
 * Get file history - when it was added and last modified
 * Works with shallow clones by starting from HEAD and working backwards
 */
export async function getFileHistories(
  dir: string,
  commits: CommitInfo[],
  onProgress?: ProgressCallback,
  knownFiles?: string[]  // Pass files from readAllRepoFiles
): Promise<Map<string, FileHistory>> {
  const fileHistories = new Map<string, FileHistory>();

  if (commits.length === 0) return fileHistories;

  // Sort commits newest to oldest (for backwards processing)
  const newestFirst = [...commits].sort(
    (a, b) => b.author.timestamp - a.author.timestamp
  );

  // Also keep oldest-first for the final result
  const oldestFirst = [...commits].sort(
    (a, b) => a.author.timestamp - b.author.timestamp
  );

  const headCommit = newestFirst[0];
  let current = 0;
  const total = newestFirst.length;

  // Use provided files or try to read from git tree
  let headFiles: string[] = knownFiles || [];

  if (headFiles.length === 0) {
    headFiles = await getFilesAtCommit(dir, headCommit.oid);
  }

  console.log(`[www] Files at HEAD: ${headFiles.length}`);

  if (headFiles.length === 0) {
    console.log(`[www] No files available for history tracking`);
    return fileHistories;
  }

  // Initialize all files as existing at HEAD
  // We'll discover their true "first commit" by walking backwards
  const fileFirstSeen = new Map<string, CommitInfo>();
  for (const file of headFiles) {
    fileFirstSeen.set(file, headCommit);
    fileHistories.set(file, {
      path: file,
      firstCommit: headCommit,
      lastCommit: headCommit,
      commitCount: 1,
      changes: []
    });
  }

  // Walk backwards through commits to find when files were added/modified
  let prevCommitOid = headCommit.oid;

  for (let i = 1; i < newestFirst.length; i++) {
    const commit = newestFirst[i];
    current++;

    if (onProgress && current % 5 === 0) {
      onProgress({
        phase: 'analyzing-history',
        current,
        total,
        message: `Analyzing commit ${current}/${total}`
      });
    }

    try {
      // Get diff between this older commit and the newer one we just processed
      const diff = await getCommitDiff(dir, commit.oid, prevCommitOid);

      // Files that were "added" going forward means they didn't exist at this commit
      // So this commit is NOT their first commit - keep looking backwards
      for (const file of diff.added) {
        const history = fileHistories.get(file);
        if (history) {
          // This file was added AFTER this commit, so this isn't its first commit
          // The previous commit we processed is its first commit
          // (We'll update firstCommit as we find earlier commits where it existed)
        }
      }

      // Files that were "modified" going forward existed at both commits
      for (const file of diff.modified) {
        const history = fileHistories.get(file);
        if (history) {
          history.commitCount++;
          // File existed at this older commit too - update firstCommit
          if (commit.author.timestamp < history.firstCommit.author.timestamp) {
            history.firstCommit = commit;
          }
          history.changes.push({ commit, type: 'modified' });
        }
      }

      // Files that were "deleted" going forward means they existed at this older commit
      // but not at the newer one (they were added back later)
      for (const file of diff.deleted) {
        // This file existed at this older commit
        const history = fileHistories.get(file);
        if (history) {
          if (commit.author.timestamp < history.firstCommit.author.timestamp) {
            history.firstCommit = commit;
          }
        }
      }

      // Update files that had no changes - they existed at this commit too
      for (const file of headFiles) {
        if (!diff.added.includes(file) && !diff.modified.includes(file) && !diff.deleted.includes(file)) {
          const history = fileHistories.get(file);
          if (history && commit.author.timestamp < history.firstCommit.author.timestamp) {
            history.firstCommit = commit;
          }
        }
      }

      prevCommitOid = commit.oid;
    } catch (err) {
      // Can't read this commit's tree - stop here
      console.log(`[www] Cannot diff commit ${commit.oid.slice(0, 7)}, stopping history scan`);
      break;
    }
  }

  // Now rebuild the changes array properly (oldest to newest)
  for (const [file, history] of fileHistories) {
    // Add the "added" event at firstCommit
    history.changes = [{ commit: history.firstCommit, type: 'added' as const }];
  }

  // Re-scan forward to build proper change history
  prevCommitOid = oldestFirst[0].oid;
  for (let i = 1; i < oldestFirst.length; i++) {
    const commit = oldestFirst[i];
    try {
      const diff = await getCommitDiff(dir, prevCommitOid, commit.oid);

      for (const file of diff.added) {
        let history = fileHistories.get(file);
        if (!history) {
          history = {
            path: file,
            firstCommit: commit,
            lastCommit: commit,
            commitCount: 1,
            changes: []
          };
          fileHistories.set(file, history);
        }
        history.changes.push({ commit, type: 'added' });
        history.lastCommit = commit;
      }

      for (const file of diff.modified) {
        const history = fileHistories.get(file);
        if (history) {
          history.changes.push({ commit, type: 'modified' });
          history.lastCommit = commit;
          history.commitCount++;
        }
      }

      for (const file of diff.deleted) {
        const history = fileHistories.get(file);
        if (history) {
          history.changes.push({ commit, type: 'deleted' });
          history.lastCommit = commit;
          history.commitCount++;
        }
      }

      prevCommitOid = commit.oid;
    } catch {
      break;
    }
  }

  console.log(`[www] Built file histories for ${fileHistories.size} files`);
  return fileHistories;
}

/**
 * Get commit checkpoints for time-lapse
 * Returns commits at regular intervals for smoother visualization
 */
export function getTimelapseCheckpoints(
  commits: CommitInfo[],
  maxCheckpoints: number = 100
): CommitInfo[] {
  if (commits.length <= maxCheckpoints) {
    return commits;
  }

  // Sort by timestamp (oldest first)
  const sorted = [...commits].sort(
    (a, b) => a.author.timestamp - b.author.timestamp
  );

  const checkpoints: CommitInfo[] = [];
  const step = (sorted.length - 1) / (maxCheckpoints - 1);

  for (let i = 0; i < maxCheckpoints; i++) {
    const index = Math.round(i * step);
    checkpoints.push(sorted[index]);
  }

  // Always include the last commit
  if (checkpoints[checkpoints.length - 1].oid !== sorted[sorted.length - 1].oid) {
    checkpoints.push(sorted[sorted.length - 1]);
  }

  return checkpoints;
}

/**
 * Get commits grouped by day for time-lapse
 */
export function getCommitsByDay(
  commits: CommitInfo[]
): Map<string, CommitInfo[]> {
  const byDay = new Map<string, CommitInfo[]>();

  for (const commit of commits) {
    const date = new Date(commit.author.timestamp * 1000);
    const dayKey = date.toISOString().split('T')[0]; // YYYY-MM-DD

    if (!byDay.has(dayKey)) {
      byDay.set(dayKey, []);
    }
    byDay.get(dayKey)!.push(commit);
  }

  return byDay;
}

/**
 * Calculate age of file in days from a reference date
 */
export function calculateFileAge(
  fileHistory: FileHistory,
  referenceTimestamp: number
): number {
  const firstCommitMs = fileHistory.firstCommit.author.timestamp * 1000;
  const referenceMs = referenceTimestamp * 1000;
  return Math.floor((referenceMs - firstCommitMs) / (1000 * 60 * 60 * 24));
}

/**
 * Timelapse data structure
 */
export interface TimelapseData {
  sortedCommits: CommitInfo[];
  filesByCommit: Map<string, Set<string>>;
  changesByCommit: Map<string, { added: string[]; modified: string[]; deleted: string[] }>;
}

/**
 * Normalize path for comparison (remove leading ./ or /, use forward slashes)
 */
function normalizePath(p: string): string {
  return p.replace(/\\/g, '/').replace(/^\.\//, '').replace(/^\//, '');
}

/**
 * Build timelapse data using file histories (works with shallow clones)
 * Instead of reading git trees, we use the already-computed file histories
 */
export async function buildTimelapseFromCommits(
  dir: string,
  commits: CommitInfo[],
  trackedFiles: Set<string>,
  onProgress?: ProgressCallback,
  fileHistories?: Map<string, FileHistory>
): Promise<TimelapseData> {
  // Sort commits oldest first
  const sortedCommits = [...commits].sort(
    (a, b) => a.author.timestamp - b.author.timestamp
  );

  const filesByCommit = new Map<string, Set<string>>();
  const changesByCommit = new Map<string, { added: string[]; modified: string[]; deleted: string[] }>();

  // Create normalized lookup for tracked files
  const normalizedTracked = new Map<string, string>();
  for (const file of trackedFiles) {
    normalizedTracked.set(normalizePath(file), file);
  }

  console.log(`[www] Building timelapse for ${sortedCommits.length} commits, tracking ${trackedFiles.size} files`);

  // If we have file histories, use them (more reliable for shallow clones)
  if (fileHistories && fileHistories.size > 0) {
    console.log(`[www] Using file histories (${fileHistories.size} entries) for timelapse`);

    // Build a map of commit -> changes from file histories
    const commitChanges = new Map<string, { added: string[]; modified: string[]; deleted: string[] }>();

    // Initialize all commits
    for (const commit of sortedCommits) {
      commitChanges.set(commit.oid, { added: [], modified: [], deleted: [] });
    }

    // Process each file history
    for (const [filePath, history] of fileHistories) {
      // Only track files in our graph
      const normalizedPath = normalizePath(filePath);
      const graphPath = normalizedTracked.get(normalizedPath);
      if (!graphPath) continue;

      for (const change of history.changes) {
        const changes = commitChanges.get(change.commit.oid);
        if (changes) {
          if (change.type === 'added') {
            changes.added.push(graphPath);
          } else if (change.type === 'modified') {
            changes.modified.push(graphPath);
          } else if (change.type === 'deleted') {
            changes.deleted.push(graphPath);
          }
        }
      }
    }

    // Now build cumulative file sets
    const currentFiles = new Set<string>();
    let filesAtStart = 0;

    for (let i = 0; i < sortedCommits.length; i++) {
      const commit = sortedCommits[i];
      const changes = commitChanges.get(commit.oid) || { added: [], modified: [], deleted: [] };

      // Apply changes
      for (const file of changes.added) {
        currentFiles.add(file);
      }
      for (const file of changes.deleted) {
        currentFiles.delete(file);
      }

      // Store snapshot
      filesByCommit.set(commit.oid, new Set(currentFiles));
      changesByCommit.set(commit.oid, changes);

      if (i === 0) {
        filesAtStart = currentFiles.size;
        console.log(`[www] First commit: ${changes.added.length} files added, ${currentFiles.size} total`);
      }

      if (onProgress && i % 10 === 0) {
        onProgress({
          phase: 'building-timelapse',
          current: i + 1,
          total: sortedCommits.length,
          message: `Building timelapse ${i + 1}/${sortedCommits.length}`
        });
      }
    }

    const lastCommit = sortedCommits[sortedCommits.length - 1];
    const finalCount = filesByCommit.get(lastCommit?.oid)?.size || 0;
    console.log(`[www] Timelapse complete: ${filesAtStart} at start -> ${finalCount} at end`);

    return { sortedCommits, filesByCommit, changesByCommit };
  }

  // Fallback: try git walk (may not work with shallow clones)
  console.log(`[www] No file histories, attempting git walk...`);

  const currentFiles = new Set<string>();
  let prevCommit: string | null = null;

  for (let i = 0; i < sortedCommits.length; i++) {
    const commit = sortedCommits[i];
    const changes = { added: [] as string[], modified: [] as string[], deleted: [] as string[] };

    if (!prevCommit) {
      const filesAtCommit = await getFilesAtCommit(dir, commit.oid);
      for (const file of filesAtCommit) {
        const normalized = normalizePath(file);
        const originalPath = normalizedTracked.get(normalized);
        if (originalPath) {
          currentFiles.add(originalPath);
          changes.added.push(originalPath);
        }
      }
    } else {
      const diff = await getCommitDiff(dir, prevCommit, commit.oid);
      for (const file of diff.added) {
        const normalized = normalizePath(file);
        const originalPath = normalizedTracked.get(normalized);
        if (originalPath) {
          currentFiles.add(originalPath);
          changes.added.push(originalPath);
        }
      }
      for (const file of diff.modified) {
        const normalized = normalizePath(file);
        const originalPath = normalizedTracked.get(normalized);
        if (originalPath) {
          changes.modified.push(originalPath);
        }
      }
      for (const file of diff.deleted) {
        const normalized = normalizePath(file);
        const originalPath = normalizedTracked.get(normalized);
        if (originalPath) {
          currentFiles.delete(originalPath);
          changes.deleted.push(originalPath);
        }
      }
    }

    filesByCommit.set(commit.oid, new Set(currentFiles));
    changesByCommit.set(commit.oid, changes);
    prevCommit = commit.oid;
  }

  return { sortedCommits, filesByCommit, changesByCommit };
}
